import React, { FunctionComponent, useEffect, useState } from 'react'
import axios from 'axios'
import 'c3/c3.css'
import { Link, useNavigate } from 'react-router-dom'
import { HostData } from '../Customer/Data'
import { LoadingDots } from '../LoadingDots/LoadingDots'
import { DonutChart } from '../Visualizations/DonutChart'
import { BarSeriesChart, SeriesPoint } from '../Visualizations/BarSeriesChart'
import { KeyInsights } from './KeyInsights'
import { AISparklesIcon } from '../Icons/Icons'
import { Chatbot } from './Chatbot'
import styles from './HealthCheckBrief.module.css'

interface Props {
  teamID: string
  host: HostData
}

// --- Domain payload (presentation-independent, mirrors backend brief.Metric) ---

interface Comparison {
  current: number
  prior: number
  percentChange: number
}

interface Segment {
  label: string
  count: number
  new?: number
}

interface CompositionValue {
  total: number
  segments: Segment[]
}

interface CountValue {
  value: number
  comparison?: Comparison
  openedInPeriod?: number
  newAdmins?: number
}

interface RatioValue {
  value: number
  comparison?: Comparison
  deviceRef?: string
}

interface SeriesValue {
  points: SeriesPoint[]
  average: number
}

// Metric is a discriminated union on `kind`; exactly one value object is present.
type Metric =
  | { id: string; kind: 'composition'; composition: CompositionValue }
  | { id: string; kind: 'count'; count: CountValue }
  | { id: string; kind: 'ratio'; ratio: RatioValue }
  | { id: string; kind: 'series'; series: SeriesValue }

// Section is a titled group of Metrics — the backend owns which Metrics group
// together and what the group is called; the frontend owns only how each
// Metric within it renders. Mirrors backend brief.Section.
interface Section {
  title: string
  metrics: Metric[]
}

interface Feature {
  id: string
  active: boolean
}

interface FeatureCategory {
  id: string
  features: Feature[]
}

interface FeatureUtilizationData {
  categories: FeatureCategory[]
}

interface HealthCheckData {
  sections: Section[]
  // featureUtilization is bespoke: its own shape, its own dedicated renderer.
  featureUtilization?: FeatureUtilizationData
}

interface Briefing {
  type: string
  data: HealthCheckData
  insights: string[]
  notes?: Record<string, any>
  createdAt: string
}

// --- Presentation: copy + formatting live on the client, not the backend ---

const CATEGORY_LABELS: Record<string, string> = {
  'security-control': 'Security & control',
  'mobility-access': 'Mobility & access',
  'automation-workflow': 'Automation & workflow',
}

const FEATURE_LABELS: Record<string, string> = {
  'secure-print-release': 'Secure Print Release',
  'find-me-printing': 'Find-Me Printing',
  watermarking: 'Digital Signatures / Watermarking',
  'mobility-print': 'Mobile Printing',
  'web-print': 'Web Print',
  'integrated-scanning': 'Integrated Scanning',
}

const METRIC_LABELS: Record<string, string> = {
  'fleet-brand': 'Fleet makeup — brand',
  'fleet-renewal': 'Fleet makeup — renewal',
  'support-tickets': 'Support tickets',
  'features-underutilized': 'Features underutilized',
  'page-volume-mono': 'Mono page volume',
  'page-volume-color': 'Colour page volume',
  'admin-logins': 'Admin logins',
  'volume-trend': 'Print volume',
}

// Per-metric presentation for series (bar-chart) tiles — bar colour and unit
// label vary per metric; value formatting stays generic via BarSeriesChart's
// valueFormat prop. Colours are theme-invariant, matching the rest of the
// app's categorical chart colours.
interface SeriesTileStyle {
  color: string
  unitLabel: string
}

const SERIES_TILE_STYLES: Record<string, SeriesTileStyle> = {
  'volume-trend': { color: '#26a726', unitLabel: 'pages' },
}
const DEFAULT_SERIES_TILE_STYLE: SeriesTileStyle = { color: '#26a726', unitLabel: 'items' }

const formatThousands = (n: number): string => Math.trunc(n).toLocaleString('en-US')

// formatPercent renders a fraction (0.011) as a one-decimal percentage ("1.1%").
const formatPercent = (fraction: number): string => `${(fraction * 100).toFixed(1)}%`

// formatCurrency renders a raw decimal string as a whole-unit currency amount.
const formatCurrency = (val: string | undefined, currencyCode: string | undefined): string => {
  if (!val) return ''
  const num = parseFloat(val)
  if (Number.isNaN(num)) return ''
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      maximumFractionDigits: 0,
    }).format(num)
  } catch {
    return num.toLocaleString('en-US', { maximumFractionDigits: 0 })
  }
}

// formatSignedPercent renders a fraction as a signed percentage with a redundant
// ".0" trimmed ("+19%", "-2.6%").
const formatSignedPercent = (fraction: number): string => {
  const pct = fraction * 100
  const s = pct.toFixed(1).replace(/\.0$/, '')
  return pct > 0 ? `+${s}%` : `${s}%`
}

const trendOf = (percentChange: number): 'up' | 'down' | '' => {
  if (percentChange > 0) return 'up'
  if (percentChange < 0) return 'down'
  return ''
}

const trendArrow = (trend?: string): React.ReactNode => {
  if (trend === 'up') return <span className={styles.trendUp}>↑</span>
  if (trend === 'down') return <span className={styles.trendDown}>↓</span>
  return null
}

// ViewTile is the rendered shape derived from a Metric — the presentation
// concern the backend used to own.
interface ViewSegment {
  label: string
  count: number
}

interface ViewTile {
  key: string
  label: string
  value: string
  subtext?: string
  trend?: 'up' | 'down' | ''
  segments?: ViewSegment[]
}

// metricView projects a raw Metric onto display copy + formatted values. All the
// per-metric presentation decisions live here, on the consumer. Excludes
// 'series' — those metrics render as SeriesTile / SeriesTileCard instead, via
// seriesTileOf.
const metricView = (m: Exclude<Metric, { kind: 'series' }>): ViewTile => {
  const id = m.id
  const label = METRIC_LABELS[id] ?? id

  if (m.kind === 'composition') {
    const segments = m.composition.segments.map(s => ({
      label: m.id === 'fleet-brand' && s.new && s.new === s.count ? `New ${s.label}` : s.label,
      count: s.count,
    }))
    return { key: m.id, label, value: formatThousands(m.composition.total), subtext: 'devices', segments }
  }

  if (m.kind === 'count') {
    const c = m.count
    const pct = c.comparison?.percentChange ?? 0
    switch (m.id) {
      case 'support-tickets':
        return {
          key: m.id,
          label,
          value: formatThousands(c.value),
          subtext: `Open (+${c.openedInPeriod ?? 0} last 30 days)`,
        }
      case 'admin-logins':
        return {
          key: m.id,
          label,
          value: formatSignedPercent(pct),
          subtext: `${c.newAdmins ?? 0} new admins`,
          trend: trendOf(pct),
        }
      default:
        return { key: m.id, label, value: formatThousands(c.value) }
    }
  }

  if (m.kind === 'ratio') {
    const r = m.ratio
    return { key: m.id, label, value: formatPercent(r.value), trend: trendOf(r.comparison?.percentChange ?? 0) }
  }

  // Unreachable for any metric matching the current Metric type.
  // Render a visible placeholder rather than crash.
  return { key: id, label, value: '—' }
}

const renderExplanation = (explanation: string[]) => {
  return explanation.map((paragraph, index) => {
    const parts = paragraph.split('**')
    return (
      <p key={index}>
        {parts.map((part, i) => {
          if (i % 2 === 1) {
            return <strong key={i}>{part}</strong>
          }
          return part
        })}
      </p>
    )
  })
}

// A fleet-makeup tile: c3 donut with the total in the centre, alongside a
// label/value legend built from the design-system `set`/`stats` list.
const MakeupTile: FunctionComponent<{ tile: ViewTile }> = ({ tile }) => (
  <div className='col half'>
    <div className='surface btm'>
      <header>
        <h4 className='btm'>{tile.label}</h4>
      </header>
      <div className='row flex flex-ver'>
        <div className='col half'>
          <DonutChart
            id={`${tile.key}-donut`}
            title={tile.value}
            data={(tile.segments ?? []).map(segment => [segment.label, segment.count])}
            style={{ height: '180px' }}
          />
        </div>
        <div className='col half'>
          <div className='set'>
            <div className='stats'>
              <ul>
                {(tile.segments ?? []).map(segment => (
                  <li key={segment.label}>
                    <span className='label'>{segment.label}</span> <span className='num'>{segment.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
)

const MetricTile: FunctionComponent<{ tile: ViewTile }> = ({ tile }) => (
  <div className='col third'>
    <div className={`surface btm ${styles.metricSurface}`}>
      <p className='blk subdued no-margin-bottom'>{tile.label}</p>
      <p className={styles.metricValue}>
        {tile.value}
        {trendArrow(tile.trend)}
      </p>
      {tile.subtext && <p className={`blk subdued no-margin-bottom ${styles.metricSubtext}`}>{tile.subtext}</p>}
    </div>
  </div>
)

// SeriesTile is the rendered shape derived from a series Metric — analogous to
// ViewTile, but carrying a whole point series rather than a single formatted
// value, since it renders as a bar chart.
interface SeriesTile {
  key: string
  label: string
  points: SeriesPoint[]
  average: number
  color: string
  unitLabel: string
}

const seriesTileOf = (m: Extract<Metric, { kind: 'series' }>): SeriesTile => {
  const style = SERIES_TILE_STYLES[m.id] ?? DEFAULT_SERIES_TILE_STYLE
  return {
    key: m.id,
    label: METRIC_LABELS[m.id] ?? m.id,
    points: m.series.points,
    average: m.series.average,
    color: style.color,
    unitLabel: style.unitLabel,
  }
}

// averageCaption contextualises the chart's average line — e.g. "Average 950
// pages/month over 12 months" — using only the months that actually have
// data, since the trailing series can start with phantom-zero months before
// a customer's earliest report (see fleet.UsagePoint.HasData). Returns null
// when no month has data yet, since an average of zero would be misleading
// rather than informative.
const averageCaption = (tile: SeriesTile): string | null => {
  const monthsWithData = tile.points.filter(p => p.hasData).length
  if (monthsWithData === 0) return null
  const monthWord = monthsWithData === 1 ? 'month' : 'months'
  return `Average ${formatThousands(tile.average)} ${tile.unitLabel}/month over ${monthsWithData} ${monthWord}`
}

// SeriesTileCard pairs its BarSeriesChart with a caption for the average
// line — the chart plus that caption is the whole story, so no separate
// latest-month headline number is shown.
const SeriesTileCard: FunctionComponent<{ tile: SeriesTile }> = ({ tile }) => {
  const caption = averageCaption(tile)
  return (
    <div className='surface btm'>
      <header>
        <h4 className='btm'>{tile.label}</h4>
      </header>
      <BarSeriesChart
        id={`${tile.key}-chart`}
        style={{ height: '200px' }}
        points={tile.points}
        average={tile.average}
        color={tile.color}
        valueFormat={formatThousands}
      />
      {caption && (
        <p
          className={`blk subdued no-margin-bottom ${styles.seriesAverageCaption}`}
          data-testid={`${tile.key}-average`}
        >
          {caption}
        </p>
      )}
    </div>
  )
}

const FeatureUtilization: FunctionComponent<{ categories: FeatureCategory[] }> = ({ categories }) => {
  if (categories.length === 0) {
    return null
  }

  const categoryLabel = (id: string) => CATEGORY_LABELS[id] ?? id
  const featureLabel = (id: string) => FEATURE_LABELS[id] ?? id

  // Reserve the first-column width on feature rows so they align with the
  // category rows, using the longest category label as the width reference.
  const longestCategory = categories.reduce((longest, category) => {
    const label = categoryLabel(category.id)
    return label.length > longest.length ? label : longest
  }, '')

  return (
    <div className={styles.sectionWrapper}>
      <header className={styles.sectionHeader}>
        <h4 className={styles.sectionTitle}>Feature utilization</h4>
      </header>
      <div className={`surface ${styles.sectionContainer}`}>
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Name</th>
              <th>Status (Actual)</th>
            </tr>
          </thead>
          <tbody>
            {categories.map(category => (
              <React.Fragment key={category.id}>
                <tr>
                  <td colSpan={3} className={styles.catRow}>
                    <strong>{categoryLabel(category.id)}</strong>
                  </td>
                </tr>
                {category.features.map(feature => (
                  <tr key={feature.id}>
                    <td>
                      <span className={styles.hiddenRow}>{longestCategory}</span>
                    </td>
                    <td>{featureLabel(feature.id)}</td>
                    <td className={feature.active ? undefined : styles.rowWarn}>{feature.active ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// SectionBlock renders one Section: its title, then its Metrics grouped by the
// kinds this build knows how to render. The kind filters are inclusion, not
// exclusion — the API response isn't runtime-validated against the Metric type,
// so a kind this frontend doesn't yet recognise (e.g. a newer backend deployed
// ahead of this build) is safely skipped rather than reaching metricView, which
// assumes well-formed input. A section whose Metrics are all unrenderable
// renders nothing, title included.
const SectionBlock: FunctionComponent<{ section: Section }> = ({ section }) => {
  const makeupTiles = section.metrics
    .filter((m): m is Extract<Metric, { kind: 'composition' }> => m.kind === 'composition')
    .map(metricView)
  const metricTiles = section.metrics
    .filter((m): m is Extract<Metric, { kind: 'count' | 'ratio' }> => m.kind === 'count' || m.kind === 'ratio')
    .map(metricView)
  const seriesTiles = section.metrics
    .filter((m): m is Extract<Metric, { kind: 'series' }> => m.kind === 'series')
    .map(seriesTileOf)

  if (makeupTiles.length === 0 && metricTiles.length === 0 && seriesTiles.length === 0) {
    return null
  }

  return (
    <section className={styles.section}>
      <header className={styles.sectionHeader}>
        <h4 className={styles.sectionTitle}>{section.title}</h4>
      </header>
      {makeupTiles.length > 0 && (
        <div className='row flex'>
          {makeupTiles.map(tile => (
            <MakeupTile key={tile.key} tile={tile} />
          ))}
        </div>
      )}
      {metricTiles.length > 0 && (
        <div className={`row flex ${styles.metricGrid}`}>
          {metricTiles.map(tile => (
            <MetricTile key={tile.key} tile={tile} />
          ))}
        </div>
      )}
      {seriesTiles.length > 0 && (
        <div className={styles.seriesSection}>
          {seriesTiles.map(tile => (
            <SeriesTileCard key={tile.key} tile={tile} />
          ))}
        </div>
      )}
    </section>
  )
}

export const HealthCheckBrief: FunctionComponent<Props> = ({ teamID, host }) => {
  const [briefing, setBriefing] = useState<Briefing | null>(null)
  const [featureCategories, setFeatureCategories] = useState<FeatureCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showNotes, setShowNotes] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const controller = new AbortController()

    setLoading(true)
    setError(null)

    axios
      .post<Briefing>(`/api/teams/${teamID}/customers/${host.id}/briefs/health-check/generate`, null, {
        signal: controller.signal,
      })
      .then(response => {
        const fetchedBriefing = response.data
        setBriefing(fetchedBriefing)
        setFeatureCategories(fetchedBriefing.data.featureUtilization?.categories ?? [])
        setLoading(false)
      })
      .catch(err => {
        if (!axios.isCancel(err)) {
          setError(err.message || 'An error occurred while generating the health check')
          setLoading(false)
        }
      })

    return () => {
      controller.abort()
    }
  }, [teamID, host.id])

  if (loading) {
    return (
      <div className={`empty loading ${styles.loadingState}`} data-testid='loading-dots'>
        <div className={styles.loadingSpinner}>
          <LoadingDots />
        </div>
        <div className={styles.loadingMessage}>
          <h4>Building your health check...</h4>
          Next time will be faster 🚀
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`alert error ${styles.errorState}`} data-testid='health-check-error'>
        <div>Failed to generate health check: {error}</div>
      </div>
    )
  }

  const sections = briefing?.data?.sections ?? []
  const notes = briefing?.notes

  return (
    <div data-testid='health-check-brief'>
      {/* Custom Header matching screenshot */}
      <div className={styles.headerDivider}>
        {/* Left Part: Back button & Breadcrumb */}
        <div className={styles.headerLeft}>
          <button onClick={() => navigate(`/intelligence-hub/${host.id}/briefs`)} className={styles.backButton}>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              className={styles.iconSubdued}
            >
              <polyline points='15 18 9 12 15 6'></polyline>
            </svg>
          </button>

          <div className={styles.breadcrumb}>
            <Link to={`/intelligence-hub/${host.id}`} className={styles.breadcrumbLink}>
              {host.name}
            </Link>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              className={styles.iconSeparator}
            >
              <polyline points='9 18 15 12 9 6'></polyline>
            </svg>
            <h2 className={styles.breadcrumbHeading}>Health check</h2>
          </div>
        </div>

        {/* Right Part: Dropdown, Download, Preview, Hide/Show Notes */}
        <div className={styles.headerRight}>
          {/* Dropdown: Showing February 2026 */}
          <div className={styles.datePill}>
            <span>
              Showing <strong>February 2026</strong>
            </span>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='14'
              height='14'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              className={styles.iconSubdued}
            >
              <polyline points='6 9 12 15 18 9'></polyline>
            </svg>
          </div>

          {/* Download Button */}
          <button className={styles.outlineButton}>Download</button>

          {/* Preview Button */}
          <button className={styles.outlineButton}>Preview</button>

          {/* Hide/Show notes Button */}
          <button
            onClick={() => setShowNotes(!showNotes)}
            className={`${styles.notesToggle}${showNotes ? ` ${styles.notesToggleActive}` : ''}`}
          >
            {showNotes ? 'Hide notes' : 'Show notes'}
          </button>
        </div>
      </div>

      {/* Layout with Chatbot Left Panel */}
      <div className={styles.briefLayout}>
        {/* Far Left Column: Chatbot */}
        <div className={styles.chatbotWrapper}>
          <Chatbot teamID={teamID} customerID={host.id} className={styles.chatbotInner} />
        </div>

        {/* Middle Column: Health check contents */}
        <div className={styles.briefContent}>
          <header className={styles.briefHeader}>
            <h3 className={styles.briefTitle}>Health check</h3>
          </header>

          {/* Layout: Highlights first, then the whole-brief AI insights, then the
              data/chart sections (Usage and performance, and later support-ticket
              volume / feature utilisation). Highlights is sections[0] by backend
              construction — see brief.buildHealthCheckSections. */}
          {sections.length > 0 && <SectionBlock section={sections[0]} />}

          <KeyInsights insights={briefing?.insights ?? []} createdAt={briefing?.createdAt} />

          {sections.slice(1).map((section, i) => (
            <SectionBlock key={section.title || i} section={section} />
          ))}

          <FeatureUtilization categories={featureCategories} />
        </div>

        {/* Right Column: Notes */}
        {showNotes && (
          <div className={styles.notesColumn}>
            <div className={styles.notesHeader}>
              <span className={styles.aiAccent}>
                <AISparklesIcon />
              </span>
              <h3 className={styles.notesHeading}>Notes</h3>
            </div>

            <div className={styles.notesBubbleWrapper}>
              {/* Speech bubble arrow — fill/stroke use scoped CSS variables so
                  they stay pixel-aligned with the bubble edge in both themes. */}
              <div className={styles.notesArrow}>
                <svg width='10' height='16' viewBox='0 0 10 16'>
                  <path d='M10 0 L0 8 L10 16' fill='var(--bubble-bg)' stroke='var(--bubble-border)' strokeWidth='1' />
                  <line x1='10' y1='1' x2='10' y2='15' stroke='var(--bubble-bg)' strokeWidth='2' />
                </svg>
              </div>

              {/* Bubble content */}
              <div className={styles.notesBody}>
                {/* Key-Value details */}
                <div className={styles.notesDetails}>
                  <div>
                    <strong>Estimated fleet value:</strong>{' '}
                    <span>{formatCurrency(notes?.estimatedFleetValue, notes?.currencyCode)}</span>
                  </div>
                  <div>
                    <strong>Estimated renewal value:</strong>{' '}
                    <span>{formatCurrency(notes?.estimatedRenewalValue, notes?.currencyCode)}</span>
                  </div>
                  <div>
                    <strong>Devices at risk:</strong> <span>{notes?.devicesAtRisk || ''}</span>
                  </div>
                  <div>
                    <strong>Risk:</strong> <span>{notes?.risk || ''}</span>
                  </div>
                </div>

                {/* Divider line */}
                <div className={styles.notesDivider} />

                {/* Paragraph notes */}
                <div data-testid='notes-explanation' className={styles.notesExplanation}>
                  {notes?.explanation ? renderExplanation(notes.explanation) : ''}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
